import Vue from 'vue';
import { mapState, mapMutations, mapActions } from 'vuex';
import ProductGallery from '@/modules/product/components/ProductGallery.vue';
import ProductRate from '@/modules/product/components/ProductRate.vue';
import ProductInfo from '@/modules/product/components/ProductInfo.vue';
import ProductAttributes from '@/modules/product/components/ProductAttributes.vue';
import ProductHighlight from '@/modules/product/components/ProductHighlight.vue';
import ProductAddToWishlist from '@/modules/product/components/ProductAddToWishlist.vue';
import ProductShare from '@/modules/product/containers/ProductShare.vue';
import ProductsSwiper from '@/components/swiper/ProductsSwiper.vue';
import ProductActions from '@/modules/product/containers/ProductActions.vue';
import Reviews from '@/containers/Reviews.vue';
import { injectSchema, injectMetaFromString } from '@/core/schema';
import Breadcrumb from '@/components/Breadcrumb.vue';
import { getCurrencyCodeFromCountry } from '@/utils/useCurrencyCode.js';
import ProductSchedule from '@/modules/product/components/ProductSchedule.vue';
import { trackEcommerce } from '@/services/analytics';
export default Vue.extend({
    components: {
        ProductGallery,
        ProductRate,
        ProductActions,
        ProductInfo,
        ProductAttributes,
        Reviews,
        ProductsSwiper,
        ProductShare,
        Breadcrumb,
        ProductSchedule,
        ProductHighlight,
        ProductAddToWishlist,
    },
    props: {
        product: Object,
        productObject: Object,
        variant: Object,
        schema: Object,
        schemaBreadcrumb: Object,
        meta: String,
    },
    created() {
        this.setCurrentProduct(this.product);
        this.setCurrentVariant(this.variant);
        this.getRecommended({ categoryUuid: this.productObject.categories.slice(-1)[0].uuid });
    },
    mounted() {
        this.startPolling();
        injectSchema([this.schema, this.schemaBreadcrumb]);
        injectMetaFromString(this.meta);
        if (this.product?.brand?.slug === 'changiassure') {
            this.$nextTick(() => this.mountChangiAssureOnce());
        }
        if (this.product?.brand?.slug === 'changifx') {
            this.$nextTick(() => this.mountChangiFxOnce());
        }
        if (this.product?.brand?.slug != 'changiassure' && this.product?.brand?.slug != 'changifx' && this.isProductReferrer() && !this.isSearchReferrer()) {
            this.setPreviousPageCategory(this.countryName);
        }
        else {
            this.setPreviousPageCategory(null);
        }
        if (this.product) {
            this.trackProductView();
        }
    },
    activated() {
        this.startPolling();
    },
    deactivated() {
        this.stopPolling();
    },
    beforeDestroy() {
        this.stopPolling();
    },
    computed: {
        ...mapState('productModule', ['currentProduct', 'currentVariant']),
        ...mapState('cartModule', ['changiAssureOrderData']),
        ...mapState('categoriesModule', ['recommended', 'recommendedLoading', 'currentCategory', 'previousPageCategory']),
        ...mapState('userModule', ['user']),
        currentUrl() {
            return window.location.href;
        },
        discountPercent() {
            const original = Number(this.product?.old_price);
            const discounted = Number(this.product?.price);
            if (!original || !discounted || discounted >= original)
                return 0;
            return Math.round(((original - discounted) / original) * 100);
        },
        countryName() {
            const country = this.schemaBreadcrumb?.itemListElement?.find(el => {
                if (!el.item)
                    return false;
                const parts = el.item.split('/').filter(Boolean);
                return parts.length === 3 &&
                    parts[0] === 'category' &&
                    parts[1] === 'popular-regions';
            });
            return country?.name || null;
        },
    },
    data() {
        return {
            _changiMounted: false,
            _changiFxMounted: false,
            checkInterval: null,
            _pollingActive: false,
            isAbleToAdd: false,
            totalAmount: 0,
        };
    },
    methods: {
        ...mapMutations('productModule', ['setCurrentProduct']),
        ...mapMutations('categoriesModule', ['setPreviousPageCategory']),
        ...mapActions('productModule', ['setCurrentVariant']),
        ...mapActions('categoriesModule', ['getRecommended']),
        ...mapActions('cartModule', ['checkCallback', 'addItem', 'getCartItems']),
        trackProductView() {
            trackEcommerce('view_item', {
                currency: 'SGD',
                value: this.product.price,
                items: [
                    {
                        item_name: this.product.name,
                        variant_name: this.currentVariant.name || '',
                        unit_name: '',
                        price: this.product.price,
                        quantity: 1,
                    },
                ],
            });
        },
        updateProductInfoByVariant() {
            this.product.name = this.currentProduct.name;
            this.product.price = this.currentVariant.price;
            this.product.old_price = this.currentVariant.old_price;
            this.$forceUpdate();
        },
        handleIsAbleToAddCart(info) {
            this.isAbleToAdd = info.isAbleToAdd;
            this.totalAmount = info.totalAmount;
        },
        scrollToId(id) {
            const element = document.getElementById(id);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            else {
                console.warn(`Element with id "${id}" not found.`);
            }
        },
        formatLargeNumber(value) {
            if (value === null || value === undefined)
                return '0';
            if (value >= 1e9)
                return (value / 1e9).toFixed(1).replace(/\.0$/, '') + 'B+';
            if (value >= 1e6)
                return (value / 1e6).toFixed(1).replace(/\.0$/, '') + 'M+';
            if (value >= 1e3)
                return (value / 1e3).toFixed(1).replace(/\.0$/, '') + 'k+';
            return value.toString();
        },
        isProductReferrer() {
            if (!document.referrer)
                return false;
            try {
                const refUrl = new URL(document.referrer);
                return refUrl.pathname.startsWith('/product/');
            }
            catch {
                return false;
            }
        },
        isSearchReferrer() {
            if (!document.referrer)
                return false;
            try {
                const refUrl = new URL(document.referrer);
                return refUrl.pathname.startsWith('/search/');
            }
            catch {
                return false;
            }
        },
        async waitForCustomElement() {
            if (customElements.get('cts-changi-assure-single-slim-order-form'))
                return;
            await customElements.whenDefined('cts-changi-assure-single-slim-order-form').catch(() => { });
        },
        // wait for FX element
        async waitForFxElement() {
            if (customElements.get('cts-changi-fx-order-form'))
                return;
            await customElements.whenDefined('cts-changi-fx-order-form').catch(() => { });
        },
        startPolling() {
            if (this.product?.brand?.slug !== 'changiassure')
                return;
            // Ensure only ONE timer exists
            this.stopPolling();
            // Poll every 5 seconds (was mislabeled as 2s)
            this.checkInterval = window.setInterval(() => {
                this.checkForCallback();
            }, 2000);
            this._pollingActive = true;
        },
        stopPolling() {
            if (this.checkInterval != null) {
                clearInterval(this.checkInterval);
                this.checkInterval = null;
            }
            this._pollingActive = false;
        },
        async startPollingWithTimeout() {
            if (this.product?.brand?.slug !== 'changiassure' || this._pollingActive)
                return;
            this._pollingActive = true;
            const tick = async () => {
                if (!this._pollingActive)
                    return;
                await this.checkForCallback();
                setTimeout(tick, 5000);
            };
            tick();
        },
        checkForCallback() {
            // this.checkCallback({});
        },
        async mountChangiAssureOnce() {
            if (this._changiMounted)
                return;
            await this.waitForCustomElement();
            const slot = this.$refs.changiSlot;
            if (!slot)
                return;
            slot.innerHTML = '';
            const el = document.createElement('cts-changi-assure-single-slim-order-form');
            let country = this.currentCategory?.title || 'Worldwide';
            if (this.currentCategory?.title !== this.previousPageCategory && this.previousPageCategory !== null) {
                country = this.previousPageCategory || 'Worldwide';
            }
            let prodType = (this.product?.slug || 'insurance-outbound');
            let productSubTypes = this.product?.product_sub_types || [];
            const attrs = {
                api_hub_domain: process.env.VUE_APP_WIDGET_PROXY_ID,
                default_language: 'en',
                product_type: productSubTypes.find(subType => subType.system_key.includes('outbound')) ? 'outbound' : 'inbound',
                payment_method: 'PaymentViaWebsite',
                callback_url: process.env.VUE_APP_API_BASE_URL + '/sales/apihub-changi-assure-callback',
                continue_url: '/cart',
                callback_data: '{}',
                cross_sell_business_unit: 'attractions',
                cross_sell_platform: 'ctstech',
                api_hub_order_uuid: '',
                changi_account_uid: this.user?.changiAccountUid || '',
                policy_holder_first_name: this.user?.firstName || this.user?.name?.split(' ')[0] || '',
                policy_holder_last_name: this.user?.lastName || this.user?.name?.split(' ')[1] || '',
                policy_holder_email_address: this.user?.email || '',
                policy_holder_contact_number: this.user?.phone || '',
                policy_holder_contact_number_country_code: this.user?.phoneCountryCode || '65',
                selected_countries: JSON.stringify([country]),
                plan: productSubTypes.find(subType => subType.system_key.includes('outbound')) ? 'Basic+' : 'Bronze',
                max_width_for_compact_view: '900',
                add_to_cart_text: 'Add to Cart',
                go_back_text: 'Go Back',
                show_go_back_button: 'true',
                continue_text: 'View Cart',
                show_box_shadow: 'false',
                collapse_coverage_items: 'true',
                show_coverage_table: 'false',
                show_plan_for_selection: 'true',
                disable_plan_for_selection: 'false',
                show_no_of_adults_selection: 'true',
                disable_no_of_adults_selection: 'false',
                show_no_of_children_selection: 'true',
                disable_no_of_children_selection: 'false',
                show_promo_code_field: 'true',
                show_plan_type_selection: 'true',
                show_arriving_from_selection: 'true',
                show_travelling_to_selection: 'true',
                show_trip_start_trip_end_fields: 'true',
                show_mobile_fields: 'true',
                show_email_fields: 'true',
                show_first_name_fields: 'true',
                show_last_name_fields: 'true',
                disable_mobile_fields: 'false',
                disable_email_fields: 'false',
                disable_first_name_fields: 'false',
                disable_last_name_fields: 'false',
                show_all_on_same_page: 'false',
                disable_arriving_from_selection: 'false',
                disable_travelling_to_selection: 'false',
                show_choice_of_trip: 'true',
                show_single_line_inputs_mobile: 'false',
                show_single_line_inputs_desktop: 'false',
                show_declarations: 'true',
                sticky_header: 'false',
                show_as_tiles: 'true',
                stack_details_vertically: 'true',
                hide_header_section: 'true',
                no_border: 'true',
                chatbot_style: 'false',
            };
            Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
            slot.appendChild(el);
            el.addEventListener('order-created', async (e) => {
                console.log('[changi] order-created', e.detail);
                await this.addToCart(e.detail.Uuid, e.detail.Order.order.order_product_units[0].uuid, e.detail);
            });
            this._changiMounted = true;
        },
        // mount the changiFX widget once
        async mountChangiFxOnce() {
            if (this._changiFxMounted)
                return;
            await this.waitForFxElement();
            const slot = this.$refs.changiFxSlot;
            if (!slot)
                return;
            slot.innerHTML = '';
            const el = document.createElement('cts-changi-fx-order-form');
            // Compose user fields
            const firstName = this.user?.firstName || '';
            const lastName = this.user?.lastName || '';
            const fullName = this.user?.name || '';
            const email = this.user?.email || '';
            const mobile = this.user?.phone || '';
            const countryCode2 = 'SG'; // keep simple; adjust if you store 2-letter codes elsewhere
            const postcode = this.user?.postalCode || '';
            // Prefer product-provided code if available
            const rateTableCode = this.product?.changiFxRateTableCode ||
                this.product?.fx_rate_table_code ||
                'NTFUHE1BDU';
            const country = this.currentCategory?.title;
            const currency = getCurrencyCodeFromCountry(country);
            const fxAttrs = {
                id: 'changiWidget',
                changi_account_uid: this.user?.changiAccountUid || '',
                api_hub_domain: process.env.VUE_APP_WIDGET_PROXY_ID,
                callback_data: '{}',
                callback_url: process.env.VUE_APP_API_BASE_URL + '/sales/apihub-changi-fx-callback',
                payment_method: 'PaymentViaWidget',
                continue_url: '/cart',
                rate_table_code: rateTableCode,
                customer_email: email,
                customer_name: fullName,
                customer_postcode: postcode,
                customer_mobile: mobile,
                two_letter_country_code: countryCode2,
                customer_date_of_birth: '',
                customer_id_type: 'passport',
                customer_id_number: '',
                initial_currency: currency || 'MYR',
                show_continue_button: 'false',
                add_to_cart_text: 'Add to Cart',
                chat_bot_style: 'false',
                show_box_shadow: 'false',
                display_on_one_page: 'true',
            };
            Object.entries(fxAttrs).forEach(([k, v]) => el.setAttribute(k, String(v)));
            slot.appendChild(el);
            // If the FX widget also fires an 'order-created' event, we reuse the same handler.
            el.addEventListener('order-created', async (e) => {
                console.log('[changi-fx] order-created', e.detail);
                try {
                    const unitUuid = e?.detail?.Order?.order?.order_product_units?.[0]?.uuid;
                    if (e.detail?.Uuid && unitUuid) {
                        await this.addToCart(e.detail.Uuid, unitUuid, e.detail);
                    }
                }
                catch (err) {
                    console.warn('FX order-created handler error', err);
                }
            });
            this._changiFxMounted = true;
        },
        async addToCart(Uuid, productUnitUuid, crossSellOrder) {
            await this.addItem({ unitUuid: productUnitUuid, quantity: 1, date: null, isCrossSell: true, crossSellUuid: Uuid, crossSellOrder: crossSellOrder });
            this.getCartItems();
        },
    },
    watch: {
        currentVariant() {
            this.updateProductInfoByVariant();
        },
        changiAssureOrderData() {
            console.log(this.changiAssureOrderData);
        },
    },
});
